import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Wrench, AlertCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface CreateRepairModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const REPAIR_TYPES = [
  { value: 'CORRECTIVE', label: 'Corrective', description: 'Repair due to damage or malfunction' },
  { value: 'PREVENTIVE', label: 'Preventive', description: 'Scheduled maintenance' },
  { value: 'WARRANTY', label: 'Warranty', description: 'Covered under warranty' },
];

const PRIORITIES = [
  { value: 'LOW', label: 'Low', description: 'Can wait' },
  { value: 'MEDIUM', label: 'Medium', description: 'Standard priority' },
  { value: 'HIGH', label: 'High', description: 'Needs attention soon' },
  { value: 'CRITICAL', label: 'Critical', description: 'Urgent - business impact' },
];

interface FormData {
  assetId: string;
  employeeId: string;
  serviceProviderId: string;
  repairType: string;
  priority: string;
  issueDescription: string;
  estimatedCost: string;
  estimatedDays: string;
}

interface FormErrors {
  assetId?: string;
  repairType?: string;
  priority?: string;
  issueDescription?: string;
  estimatedCost?: string;
  estimatedDays?: string;
}

export default function CreateRepairModal({ open, onClose, onSuccess }: CreateRepairModalProps) {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<FormData>({
    assetId: '',
    employeeId: '',
    serviceProviderId: '',
    repairType: 'CORRECTIVE',
    priority: 'MEDIUM',
    issueDescription: '',
    estimatedCost: '',
    estimatedDays: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Asset search state
  const [assetSearch, setAssetSearch] = useState('');
  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const assetDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assetDropdownRef.current && !assetDropdownRef.current.contains(event.target as Node)) {
        setAssetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch assets with search
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['assetsForRepair', assetSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      if (assetSearch) params.append('search', assetSearch);
      const response = await api.get(`/assets?${params.toString()}`);
      return response.data.data || [];
    },
    enabled: open,
  });

  // Fetch employees
  const { data: employeesData } = useQuery({
    queryKey: ['employeesForRepair'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=100&status=ACTIVE');
      return response.data.data || [];
    },
    enabled: open,
  });

  // Fetch service providers
  const { data: providersData } = useQuery({
    queryKey: ['serviceProvidersForRepair'],
    queryFn: async () => {
      const response = await api.get('/service-providers?limit=100');
      return response.data.data || [];
    },
    enabled: open,
  });

  const assets = assetsData || [];
  const employees = employeesData || [];
  const providers = providersData || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = {
        assetId: data.assetId || null,
        employeeId: data.employeeId || null,
        serviceProviderId: data.serviceProviderId || null,
        repairType: data.repairType,
        priority: data.priority,
        issueDescription: data.issueDescription,
        estimatedCost: data.estimatedCost ? parseFloat(data.estimatedCost) : null,
        estimatedDays: data.estimatedDays ? parseInt(data.estimatedDays) : null,
      };
      const response = await api.post('/repairs', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      queryClient.invalidateQueries({ queryKey: ['sanalam-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to create repair');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.assetId) {
      newErrors.assetId = 'Please select an asset';
    }

    if (!formData.repairType) {
      newErrors.repairType = 'Please select a repair type';
    }

    if (!formData.priority) {
      newErrors.priority = 'Please select a priority';
    }

    if (!formData.issueDescription.trim()) {
      newErrors.issueDescription = 'Issue description is required';
    }

    if (!formData.estimatedCost || parseFloat(formData.estimatedCost) < 0) {
      newErrors.estimatedCost = 'Estimated cost is required';
    }

    if (!formData.estimatedDays || parseInt(formData.estimatedDays) < 1) {
      newErrors.estimatedDays = 'Estimated days is required (minimum 1)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    createMutation.mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      assetId: '',
      employeeId: '',
      serviceProviderId: '',
      repairType: 'CORRECTIVE',
      priority: 'MEDIUM',
      issueDescription: '',
      estimatedCost: '',
      estimatedDays: '',
    });
    setErrors({});
    setSubmitError(null);
    setAssetSearch('');
    setSelectedAsset(null);
    setAssetDropdownOpen(false);
  };

  const handleClose = () => {
    resetForm();
    setAssetSearch('');
    setSelectedAsset(null);
    setAssetDropdownOpen(false);
    onClose();
  };

  // Handle asset selection
  const handleAssetSelect = (asset: any) => {
    setSelectedAsset(asset);
    setFormData(prev => ({ ...prev, assetId: asset.id }));
    setAssetSearch('');
    setAssetDropdownOpen(false);
    
    // Auto-populate employee if assigned
    if (asset.assignedToId) {
      setFormData(prev => ({ ...prev, assetId: asset.id, employeeId: asset.assignedToId }));
    }
    
    if (errors.assetId) {
      setErrors(prev => ({ ...prev, assetId: undefined }));
    }
  };

  // Clear selected asset
  const handleClearAsset = () => {
    setSelectedAsset(null);
    setFormData(prev => ({ ...prev, assetId: '', employeeId: '' }));
    setAssetSearch('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Create New Repair
              </CardTitle>
              <CardDescription>
                Record a new repair for an asset
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {submitError && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Asset Selection - Searchable */}
              <div className="space-y-2">
                <Label htmlFor="assetSearch">
                  Asset <span className="text-destructive">*</span>
                </Label>
                <div className="relative" ref={assetDropdownRef}>
                  {selectedAsset ? (
                    <div className={cn(
                      "flex items-center justify-between w-full h-10 px-3 rounded-md border bg-background text-sm",
                      errors.assetId ? "border-destructive" : "border-input"
                    )}>
                      <span>
                        {selectedAsset.assetTag} - {selectedAsset.name}
                        {selectedAsset.assignedTo && ` (${selectedAsset.assignedTo.firstName} ${selectedAsset.assignedTo.lastName})`}
                      </span>
                      <button
                        type="button"
                        onClick={handleClearAsset}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="assetSearch"
                          placeholder="Search by asset tag, name, serial, branch, or employee..."
                          className={cn(
                            "pl-9",
                            errors.assetId ? "border-destructive" : ""
                          )}
                          value={assetSearch}
                          onChange={(e) => {
                            setAssetSearch(e.target.value);
                            setAssetDropdownOpen(true);
                          }}
                          onFocus={() => setAssetDropdownOpen(true)}
                        />
                      </div>
                      {assetDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {assetsLoading ? (
                            <div className="p-3 text-sm text-muted-foreground">Loading...</div>
                          ) : assets.length === 0 ? (
                            <div className="p-3 text-sm text-muted-foreground">
                              {assetSearch ? 'No assets found' : 'Type to search assets'}
                            </div>
                          ) : (
                            assets.map((asset: any) => (
                              <button
                                key={asset.id}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center justify-between"
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <div>
                                  <span className="font-medium">{asset.assetTag}</span>
                                  <span className="text-muted-foreground"> - {asset.name}</span>
                                  {asset.serialNumber && (
                                    <span className="text-xs text-muted-foreground ml-2">SN: {asset.serialNumber}</span>
                                  )}
                                  <div className="text-xs text-muted-foreground">
                                    {asset.branch?.name && <span>{asset.branch.name}</span>}
                                    {asset.branch?.name && asset.assignedTo && <span> • </span>}
                                    {asset.assignedTo && (
                                      <span>Assigned: {asset.assignedTo.firstName} {asset.assignedTo.lastName}</span>
                                    )}
                                  </div>
                                </div>
                                <span className={cn(
                                  "text-xs px-2 py-0.5 rounded shrink-0 ml-2",
                                  asset.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' :
                                  asset.status === 'ASSIGNED' ? 'bg-blue-100 text-blue-700' :
                                  asset.status === 'IN_MAINTENANCE' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                )}>
                                  {asset.status}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {errors.assetId && (
                  <p className="text-sm text-destructive">{errors.assetId}</p>
                )}
              </div>

              {/* Employee Selection */}
              <div className="space-y-2">
                <Label htmlFor="employeeId">Employee (Optional)</Label>
                <select
                  id="employeeId"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                >
                  <option value="">Select an employee</option>
                  {employees.map((emp: any) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.employeeId})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Employee responsible for the asset at time of issue
                </p>
              </div>

              {/* Service Provider */}
              <div className="space-y-2">
                <Label htmlFor="serviceProviderId">Service Provider (Optional)</Label>
                <select
                  id="serviceProviderId"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.serviceProviderId}
                  onChange={(e) => setFormData({ ...formData, serviceProviderId: e.target.value })}
                >
                  <option value="">Select a service provider</option>
                  {providers.map((provider: any) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Repair Type */}
              <div className="space-y-2">
                <Label>
                  Repair Type <span className="text-destructive">*</span>
                </Label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {REPAIR_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-left",
                        formData.repairType === type.value
                          ? "border-primary bg-primary/5"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, repairType: type.value });
                        if (errors.repairType) {
                          setErrors(prev => ({ ...prev, repairType: undefined }));
                        }
                      }}
                    >
                      <p className="font-medium text-sm">{type.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    </button>
                  ))}
                </div>
                {errors.repairType && (
                  <p className="text-sm text-destructive">{errors.repairType}</p>
                )}
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label>
                  Priority <span className="text-destructive">*</span>
                </Label>
                <div className="grid gap-2 sm:grid-cols-4">
                  {PRIORITIES.map((priority) => (
                    <button
                      key={priority.value}
                      type="button"
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-left",
                        formData.priority === priority.value
                          ? "border-primary bg-primary/5"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, priority: priority.value });
                        if (errors.priority) {
                          setErrors(prev => ({ ...prev, priority: undefined }));
                        }
                      }}
                    >
                      <p className="font-medium text-sm">{priority.label}</p>
                      <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{priority.description}</p>
                    </button>
                  ))}
                </div>
                {errors.priority && (
                  <p className="text-sm text-destructive">{errors.priority}</p>
                )}
              </div>

              {/* Issue Description */}
              <div className="space-y-2">
                <Label htmlFor="issueDescription">
                  Issue Description <span className="text-destructive">*</span>
                </Label>
                <textarea
                  id="issueDescription"
                  className={cn(
                    "w-full min-h-[100px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                    errors.issueDescription ? "border-destructive focus:ring-destructive" : "border-input"
                  )}
                  placeholder="Describe the issue in detail..."
                  value={formData.issueDescription}
                  onChange={(e) => {
                    setFormData({ ...formData, issueDescription: e.target.value });
                    if (errors.issueDescription) {
                      setErrors(prev => ({ ...prev, issueDescription: undefined }));
                    }
                  }}
                />
                {errors.issueDescription && (
                  <p className="text-sm text-destructive">{errors.issueDescription}</p>
                )}
              </div>

              {/* Cost & Time Estimates */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="estimatedCost">
                    Estimated Cost <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="estimatedCost"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className={errors.estimatedCost ? "border-destructive" : ""}
                    value={formData.estimatedCost}
                    onChange={(e) => {
                      setFormData({ ...formData, estimatedCost: e.target.value });
                      if (errors.estimatedCost) {
                        setErrors(prev => ({ ...prev, estimatedCost: undefined }));
                      }
                    }}
                  />
                  {errors.estimatedCost && (
                    <p className="text-sm text-destructive">{errors.estimatedCost}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimatedDays">
                    Estimated Days <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="estimatedDays"
                    type="number"
                    min="1"
                    placeholder="5"
                    className={errors.estimatedDays ? "border-destructive" : ""}
                    value={formData.estimatedDays}
                    onChange={(e) => {
                      setFormData({ ...formData, estimatedDays: e.target.value });
                      if (errors.estimatedDays) {
                        setErrors(prev => ({ ...prev, estimatedDays: undefined }));
                      }
                    }}
                  />
                  {errors.estimatedDays && (
                    <p className="text-sm text-destructive">{errors.estimatedDays}</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create Repair'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
